import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import type { UserDoc } from '../../shared/types';
import { CHAT_NAME, DAILY_NAME } from '../../shared/constants';
import { CropPhoto } from './CropPhoto';
import { avatarOf } from './data';
import { BackCloses } from './router';
import { enqueueUpload } from './uploadQueue';
import { Avatar, IconCamera, IconClose } from './ui';

/*
 * How far this phone has got with asking. Per phone, not on the user doc: it
 * is only ever a nudge, and a second phone asking once more costs nothing.
 *
 * 'asked'  -- the sheet was put off, so the banner stands in for it.
 * 'closed' -- the banner was closed too, and nothing asks again.
 */
const STAGE_KEY = 'givisnap:photoPrompt';
/*
 * The pointer at your face in the corner, once, after the sheet. Closing the
 * banner shows it regardless.
 */
const TIP_KEY = 'givisnap:photoTip';

type Stage = 'new' | 'asked' | 'closed';

function readStage(): Stage {
  try {
    const stored = localStorage.getItem(STAGE_KEY);
    return stored === 'asked' || stored === 'closed' ? stored : 'new';
  } catch {
    return 'new';
  }
}

function tipSeen(): boolean {
  try {
    return localStorage.getItem(TIP_KEY) === '1';
  } catch {
    return false;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Blocked storage: it asks again next launch, which is harmless.
  }
}

/**
 * Asking for a profile photo, for anyone who has not chosen one of their own.
 *
 * A Google picture does not count: it is whatever that account happened to
 * have, not a face picked for the family. First a sheet as the app opens;
 * put off, a banner under the header until a photo is added or the banner
 * closed. Either way it ends by pointing at your face in the corner, which is
 * where the photo is changed from then on.
 *
 * Drawn by the chat, under its header, so the banner and the pointer sit
 * right below the face they are about.
 */
export function PhotoPrompt({ uid, user }: { uid: string; user: UserDoc | undefined }) {
  const [stage, setStage] = useState<Stage>(readStage);
  const [tip, setTip] = useState<'added' | 'later' | 'closed' | null>(null);
  const [picked, setPicked] = useState<File | null>(null);
  // Added in this session: the upload lands on the user doc a moment later,
  // and nothing should ask again in between.
  const [added, setAdded] = useState(false);
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);

  // Not until the user doc is here, or everyone is asked for a moment on open.
  if (!user) return null;
  const wanting = !user.photo && !added;
  const name = user.displayName || '?';

  const point = (why: 'added' | 'later') => {
    if (!tipSeen()) setTip(why);
  };
  const putOff = () => {
    setStage('asked');
    store(STAGE_KEY, 'asked');
    point('later');
  };
  const close = () => {
    setStage('closed');
    store(STAGE_KEY, 'closed');
    // Nothing asks again after this, so where to find it is said once more,
    // even to someone who was shown it when the sheet was put off.
    setTip('closed');
  };
  const onCropped = (file: File) => {
    setPicked(null);
    setAdded(true);
    enqueueUpload({ kind: 'avatar', uid, file });
    point('added');
  };
  const gotIt = () => {
    setTip(null);
    store(TIP_KEY, '1');
  };

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    setPicked(event.target.files?.[0] ?? null);
    // Cleared, or the same file twice in a row fires nothing the second time.
    event.target.value = '';
  };

  const inputs = (
    <>
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="user"
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={pick}
      />
      <input
        ref={library}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={pick}
      />
    </>
  );

  if (picked) {
    return createPortal(
      <div className="photo-crop">
        <BackCloses onBack={() => setPicked(null)} />
        <CropPhoto file={picked} onCancel={() => setPicked(null)} onDone={onCropped} />
      </div>,
      document.body,
    );
  }

  // What it says about the face you have now: an initial, or Google's.
  const why = user.photoURL
    ? `Pick the face the family sees on your snaps, in ${CHAT_NAME} and on your ${DAILY_NAME}s.`
    : `So the family sees your face on your snaps, in ${CHAT_NAME} and on your ${DAILY_NAME}s, not just your initial.`;

  return (
    <>
      {tip && (
        <div className="photo-tip" role="status">
          <span className="photo-tip-arrow" aria-hidden="true" />
          <span className="photo-tip-text">
            {tip === 'added'
              ? 'Your photo is on its way. Tap your face up here to change it in your profile.'
              : tip === 'closed'
                ? 'No problem. Tap your face up here any time to add one in your profile.'
                : 'You can add one any time. Tap your face up here to change it in your profile.'}
          </span>
          <button type="button" className="photo-tip-ok" onClick={gotIt}>
            Got it
          </button>
        </div>
      )}

      {wanting && stage === 'asked' && !tip && (
        <div className="photo-banner">
          <span className="photo-banner-face">
            <Avatar name={name} photoURL={avatarOf(user)} size={40} />
            <span className="photo-banner-plus" aria-hidden="true">
              +
            </span>
          </span>
          <span className="photo-banner-text">
            <span className="photo-banner-title">Add your photo</span>
            <span className="photo-banner-line">
              {user.photoURL ? 'Pick the face everyone sees.' : 'So everyone sees your face, not just your initial.'}
            </span>
          </span>
          <button type="button" className="photo-banner-add" onClick={() => library.current?.click()}>
            Add
          </button>
          <button type="button" className="photo-banner-close" aria-label="Not now" onClick={close}>
            <IconClose size={16} />
          </button>
        </div>
      )}

      {wanting &&
        stage === 'new' &&
        createPortal(
          <div className="photo-sheet-scrim" onClick={putOff}>
            <BackCloses onBack={putOff} />
            <div
              className="photo-sheet"
              role="dialog"
              aria-label="Add a profile photo"
              onClick={(event) => event.stopPropagation()}
            >
              <span className="snap-sheet-grip" aria-hidden="true" />
              <span className="photo-sheet-face">
                <Avatar name={name} photoURL={avatarOf(user)} size={96} />
                <span className="photo-sheet-badge" aria-hidden="true">
                  <IconCamera size={18} />
                </span>
              </span>
              <span className="photo-sheet-title">Add a profile photo</span>
              <span className="photo-sheet-why">{why}</span>
              <button type="button" className="btn btn--block" onClick={() => camera.current?.click()}>
                Take a photo
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={() => library.current?.click()}
              >
                Choose a photo
              </button>
              <button type="button" className="photo-sheet-later" onClick={putOff}>
                Not now
              </button>
            </div>
          </div>,
          document.body,
        )}

      {/* Last, so the tip or the banner is the first thing under the header. */}
      {inputs}
    </>
  );
}
